import { useRef, useEffect } from 'react';
import { StateValue } from 'xstate';
import { useApplicationMachine } from '../../ApplicationMachineContext';
import { useBuyerMachine } from '../../BuyerMachineContext';
import { RouteMachineServiceEventEmitter } from '../types/events';

/**
 * Subscribes to the buyer and application machine state and emits
 * and event with the buyer is complete, and the application is approved
 * @param routingMachineService RouteMachineServiceEventEmitter
 */
export const useBuyerApplicationEventEmitter = (
  routingMachineService: RouteMachineServiceEventEmitter
): void => {
  const { state: buyerState } = useBuyerMachine();
  const { state: applicationState } = useApplicationMachine();
  const lastApplicationStateRef = useRef<StateValue>(undefined);

  // Compound state subscriber
  useEffect(() => {
    if (applicationState.matches({ ready: 'invalid' })) {
      // Complete buyer with invalid application
      if (buyerState.matches({ ready: { complete: 'initial' } })) {
        routingMachineService.send({
          type: 'SEND_ROUTE_INVALID_APPLICATION_COMPLETE_BUYER',
        });
        // Move buyer to next step and instead of creating another potentially invalid application
      } else if (
        buyerState.matches({ ready: { complete: 'contactUpdated' } }) ||
        buyerState.matches({
          ready: { complete: 'contactUpdatedWithoutChange' },
        })
      ) {
        routingMachineService.send({
          type: 'SEND_ROUTE_INVALID_APPLICATION_COMPLETE_BUYER',
        });
      }
      return;
    }

    // Prevents over firing of complete event when updating buyer contact information
    if (
      lastApplicationStateRef.current &&
      applicationState.matches(
        lastApplicationStateRef.current as Record<string, string>
      )
    ) {
      return;
    }

    if (applicationState.matches({ ready: 'approved' })) {
      // Has complete buyer and approved application
      if (
        buyerState.matches({
          ready: {
            complete: 'initial',
          },
        })
      ) {
        routingMachineService.send({
          type: 'SEND_ROUTE_APPROVED_COMPLETE_INITIAL_BUYER',
        });
        // Updated billing address or contact information
      } else if (
        buyerState.matches({ ready: { complete: 'contactUpdated' } }) ||
        buyerState.matches({
          ready: { complete: 'contactUpdatedWithoutChange' },
        })
      ) {
        routingMachineService.send({
          type: 'SEND_ROUTE_APPROVED_COMPLETE_CONTACT_UPDATED_BUYER',
        });
        // Updated iin dob
      } else if (
        buyerState.matches({ ready: { complete: 'dobUpdated' } }) ||
        buyerState.matches({ ready: { complete: 'dobUpdatedWithoutChange' } })
      ) {
        routingMachineService.send({
          type: 'SEND_ROUTE_APPROVED_COMPLETE_DOB_UPDATED_BUYER',
        });
      } else if (buyerState.matches({ ready: { complete: 'emailUpdated' } })) {
        routingMachineService.send({
          type: 'SEND_ROUTE_APPROVED_COMPLETE_EMAIL_UPDATED_BUYER',
        });
      }
    }
    lastApplicationStateRef.current = applicationState.value;
  }, [buyerState, applicationState]);
};
